import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Copy, Share2, Users, Coins, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function Referral() {
  const [data, setData] = useState({ referral_code: "", referrals_count: 0, target: 5 });

  useEffect(() => {
    api.get("/referral/stats").then(r => setData(r.data));
  }, []);

  const link = `${window.location.origin}/signup?ref=${data.referral_code}`;
  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Link copied!"); };
  const share = async () => {
    const text = `Join me on NextScene — get discovered as a talent from your town. Use my code ${data.referral_code}: ${link}`;
    if (navigator.share) { try { await navigator.share({ title: "NextScene", text, url: link }); return; } catch {} }
    await navigator.clipboard.writeText(text);
    toast.success("Referral message copied!");
  };

  const progress = Math.min(100, (data.referrals_count / data.target) * 100);

  return (
    <div data-testid="referral-page" className="min-h-screen pb-24">
      <TopBar subtitle="Refer & earn"/>
      <div className="max-w-2xl mx-auto px-4 pt-4 space-y-4">
        <BackButton fallback="/app" label="Back"/>

        <Link data-testid="open-rewards-dashboard" to="/app/rewards" className="block rounded-2xl border border-[#F5C462]/30 bg-gradient-to-r from-[#2a1f0e] via-[#180D0D] to-[#0A0605] p-4 hover:border-[#F5C462]/60 transition-colors">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#F5C462] flex items-center gap-1"><Coins size={12}/> Rewards Dashboard</p>
              <p className="mt-1 font-display text-lg font-semibold text-white">See coins, milestones & leaderboard</p>
              <p className="text-xs text-zinc-400 mt-0.5">Silver → Plus → Pro tiers · weekly leaderboard · spend coins on perks</p>
            </div>
            <ArrowUpRight className="text-[#F5C462] shrink-0" size={20}/>
          </div>
        </Link>

        <div className="rounded-3xl border border-[#DC2626]/30 bg-gradient-to-br from-[#DC2626]/10 to-[#180D0D] p-6">
          <div className="w-12 h-12 rounded-2xl bg-[#DC2626] text-black flex items-center justify-center mb-4">
            <Users/>
          </div>
          <h1 className="font-display text-3xl font-bold">Bring 5 friends. <span className="gold-text">Get Plus free.</span></h1>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            Every new user who signs up with your code counts. Hit 5 and Plus is auto-activated on your account for 30 days.
          </p>
          <div className="mt-6">
            <p className="text-xs text-zinc-400 uppercase tracking-wider">Your code</p>
            <div className="mt-1 flex items-center gap-2">
              <code data-testid="referral-code" className="font-display text-3xl font-bold text-[#DC2626] tracking-widest">{data.referral_code}</code>
            </div>
          </div>
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-zinc-400">{data.referrals_count} / {data.target} referrals</span>
              <span className="text-[#DC2626]">{Math.max(0, data.target - data.referrals_count)} to go</span>
            </div>
            <div className="h-2 rounded-full bg-zinc-800 overflow-hidden">
              <div className="h-full bg-[#DC2626] transition-all duration-500" style={{ width: `${progress}%` }}/>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <Button data-testid="ref-copy" onClick={copy} className="h-11 rounded-full bg-zinc-900 hover:bg-zinc-800 text-white border border-zinc-700"><Copy size={14} className="mr-1.5"/> Copy link</Button>
            <Button data-testid="ref-share" onClick={share} className="h-11 rounded-full bg-[#DC2626] hover:bg-[#EF4444] text-black font-semibold"><Share2 size={14} className="mr-1.5"/> Share</Button>
          </div>
        </div>

        <div className="rounded-2xl border border-zinc-800 bg-[#180D0D] p-5 text-sm text-zinc-300 leading-relaxed">
          <p className="font-semibold text-white mb-2">How it works</p>
          <ol className="space-y-1.5 list-decimal ml-5">
            <li>Share your code or link with 5 friends.</li>
            <li>They sign up and complete their profile.</li>
            <li>Plus plan activates on your account for 30 days, automatically.</li>
          </ol>
          <Link to="/app/referral-card" data-testid="ref-story-card-link" className="mt-3 inline-flex items-center gap-1 text-xs text-[#DC2626] hover:underline font-semibold">
            Grab a share-ready story card →
          </Link>
        </div>
      </div>
    </div>
  );
}
